import { EntityType } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import { ReviewExtendedClient, ReviewTransactionClient } from '@frontend/review/api';
import { reviewRequest } from '@frontend/review/events';
import { IExtendedReview, IReview, IReviewTransaction } from '@frontend/review/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { CategoriesContainer } from '../../applications/modals/containers/categories-container';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { ReactiveDetailRegistryEntry } from '../../modal/reactive-detail-shell/reactive-detail.types';
import { useAppDispatch } from '../../redux/store';

export const ReviewDetailsCard = ({ review }: { review: IExtendedReview }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={review}
            cols={2}
            order={['user', 'product', 'outcome', 'expected', 'actual', 'creation_timestamp', 'update_timestamp']}
            valueOverwrite={
                new Map<string, (obj: IExtendedReview) => React.ReactNode>([
                    [
                        'user',
                        (obj) =>
                            obj.user ? (
                                <PlatformObjectBadge
                                    entityType={obj.user.entity_type}
                                    id={obj.user.entity_id}
                                    display={obj.user.name}
                                    foreign={obj.user.foreign}
                                    accountId={accountIdFromResolvedEntity(obj.user)}
                                />
                            ) : (
                                '/'
                            )
                    ],
                    [
                        'product',
                        (obj) =>
                            obj.product ? (
                                <PlatformObjectBadge
                                    entityType={obj.product.entity_type}
                                    id={obj.product.entity_id}
                                    display={obj.product.name}
                                    foreign={obj.product.foreign}
                                    accountId={accountIdFromResolvedEntity(obj.product)}
                                />
                            ) : (
                                '/'
                            )
                    ]
                ])
            }
        />
    </div>
);

export const ReviewCategoriesSection = ({ review }: { review: IExtendedReview }) => (
    <CategoriesContainer
        entity_id={review.id}
        entity_type={EntityType.REVIEW}
        categories={review.categories}
    />
);

export const ReviewTransactionsSection = ({ review }: { review: IExtendedReview }) => {
    const dispatch = useAppDispatch();
    return (
        <ReactiveObjectList<IReviewTransaction>
            id={`review-${review.id}-transactions`}
            dispatch={dispatch}
            fetch={(arg) => ReviewTransactionClient.fetchReviewTransactions({ ...arg, review_id: review.id })}
            type={EntityType.REVIEW_TRANSACTION}
            columns={{ columns: ['transaction_id', 'create_timestamp'] }}
            label={CommonMessage.OBJECTS.TRANSACTION.PLURAL}
            showCount
            tableProps={{ defaultHeight: true }}
        />
    );
};

export const reviewReactiveDetail: ReactiveDetailRegistryEntry<IReview, IExtendedReview> = {
    entityType: EntityType.REVIEW,
    resolveFunction: reviewRequest,
    fetchExtended: ({ object }) => ReviewExtendedClient.fetchExtendedReview(object.account_id, object.id),
    modalClassName: 'w-full lg:w-2/3 max-w-[900px]',
    title: () => (
        <FormattedMessage
            id='review.detail.title'
            defaultMessage='Review detail'
        />
    ),
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) =>
                view.extended ? (
                    <>
                        <ReviewDetailsCard review={view.extended} />
                        <ReviewCategoriesSection review={view.extended} />
                    </>
                ) : null
        },
        {
            id: 'transactions',
            label: CommonMessage.OBJECTS.TRANSACTION.PLURAL,
            render: (view) => (view.extended ? <ReviewTransactionsSection review={view.extended} /> : null)
        }
    ]
};
